import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { timeConverter } from 'src/common/config/FunctionHelper';

export interface AgentLastRunProps {
  /** Explicit timestamp string (ISO date), epoch number (ms or s), or Date object */
  timestamp?: string | number | Date | null;
  /** Optional custom CSS classes */
  className?: string;
  /** Optional show border */
  showBorder?: boolean;
}

/**
 * Safely parses any date/timestamp representation and formats it using the user's date format and time.
 *
 * @param timestamp - The timestamp string, number, or Date
 * @param dateFormat - The user's preferred date format ('DD/MM/YYYY' | 'MM/DD/YYYY')
 * @returns Formatted date & time string or empty string if invalid/missing
 */
export const formatAgentLastRunTime = (
  timestamp: string | number | Date | null | undefined,
  dateFormat: string = 'DD/MM/YYYY'
): string => {
  if (!timestamp || timestamp === '0' || timestamp === 0) return '';
  let ms: number;

  if (typeof timestamp === 'number') {
    if (timestamp <= 0) return '';
    ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  } else if (typeof timestamp === 'string') {
    const trimmed = timestamp.trim();
    if (
      !trimmed ||
      trimmed === '0' ||
      trimmed === 'null' ||
      trimmed === 'undefined'
    )
      return '';
    const num = Number(trimmed);
    if (!isNaN(num)) {
      if (num <= 0) return '';
      ms = num < 10000000000 ? num * 1000 : num;
    } else {
      ms = new Date(trimmed).getTime();
    }
  } else if (timestamp instanceof Date) {
    ms = timestamp.getTime();
  } else {
    return '';
  }

  if (isNaN(ms) || ms <= 0) return '';
  const safeDateFormat =
    dateFormat === 'MM/DD/YYYY' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  return timeConverter(ms, safeDateFormat, false, false, true);
};

/**
 * `AgentLastRun` displays the "Last run at {date/time}" indicator for Orbit AI agent pages.
 * If no valid timestamp is available, it renders null.
 */
export const AgentLastRun: React.FC<AgentLastRunProps> = ({
  timestamp,
  className = '',
  showBorder = false
}) => {
  const userDetails = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  const dateFormat = userDetails?.date_format || 'DD/MM/YYYY';

  const formattedDateTime = useMemo(() => {
    return formatAgentLastRunTime(timestamp, dateFormat);
  }, [timestamp, dateFormat]);

  if (!formattedDateTime) {
    return null;
  }

  return (
    <div
      className={`${
        showBorder
          ? 'border px-3 py-1 rounded-lg border-[var(--color-border-light)] shadow-sm'
          : ''
      } text-sm font-medium text-[#475467] whitespace-nowrap select-none ${className}`}
      aria-label="agent-last-run"
    >
      Last run at {formattedDateTime}
    </div>
  );
};

export default AgentLastRun;
